import { ISubscriber } from '@studyportals/event-aggregation-service-interface';
import {
	IAppRegistration,
	IAppState,
	SearchType,
	SearchPageAppRegistrationRequestedEvent,
	SearchPageRouterReadyEvent,
	IFilterSelection,
} from '@studyportals/search-page-interface';
import { Dependencies } from '@/platform/di/Dependencies';
import { Container } from '@/platform/di/Container';
import { EventAggregationService } from '@/platform/EventAggregationService';
import { Routing } from '@/router/Routing';
import { FilterSelection } from '@/filters/selection/FilterSelection';
import { FilterOptions } from '@/filters/options/FilterOptions';
import { FilterKey } from '@studyportals/search-filters';
import { FilterSelectionState } from './filters/selection/FilterSelectionState';

export type DependenciesOfOrganisationsSearch = Dependencies;

export class OrganisationsSearch implements ISubscriber<SearchPageRouterReadyEvent> {
	private static _instance: OrganisationsSearch | null = null;

	private readonly eventAggregationService: EventAggregationService;
	private readonly routing: Routing;
	private readonly filterSelection: FilterSelection;
	private readonly filterOptions: FilterOptions;

	public readonly container: Container;

	public constructor(dependencies?: DependenciesOfOrganisationsSearch) {
		this.container = Container.configure(dependencies);

		this.routing = this.container.resolve(Routing);
		this.filterSelection = this.container.resolve(FilterSelection);
		this.filterOptions = this.container.resolve(FilterOptions);

		this.eventAggregationService = this.container.resolve(EventAggregationService);
		this.eventAggregationService.subscribeTo(SearchPageRouterReadyEvent.EventType, this, true);
	}

	public static get instance(): OrganisationsSearch {
		if (OrganisationsSearch._instance === null) {
			OrganisationsSearch._instance = new OrganisationsSearch();
		}

		return OrganisationsSearch._instance;
	}

	public notify(event: SearchPageRouterReadyEvent): void {
		if (SearchPageRouterReadyEvent.is(event)) {
			this.routing.initialize(event.routing);
			this.register();
		}
	}

	public static initialize(dependencies?: DependenciesOfOrganisationsSearch): void {
		OrganisationsSearch._instance = new OrganisationsSearch(dependencies);
	}

	private register(): void {
		this.eventAggregationService.publish(new SearchPageAppRegistrationRequestedEvent(this.createAppRegistration()));
	}

	private activate(state: IAppState): void {
		this.filterSelection.replaceFor(this.createFilterSelectionState(state));
	}

	private sleep(): IAppState {
		const result: IFilterSelection = {};

		this.filterSelection.getKeys().forEach((key) => {
			const selection = this.filterSelection.getFor(key);
			result[key] = selection;
		});

		return { filterSelection: result };
	}

	private initializeFrom(state: IAppState): void {
		this.filterSelection.initializeFrom(this.createFilterSelectionState(state));
	}

	private isPathForThisApp(path: string): boolean {
		const regex = /^\/universities\/(master|bachelor|phd|course|distance-learning)(\/|$)/;
		return regex.test(path);
	}

	private createAppRegistration(): IAppRegistration {
		return {
			type: SearchType.ORGANISATION,
			availableFilters: this.filterOptions.identifyFilters(),
			routing: this.routing.createRoutingRegistration(),
			activate: (state): void => this.activate(state),
			sleep: (): IAppState => this.sleep(),
			initializeFrom: (state): void => this.initializeFrom(state),
			isPathForThisApp: (path): boolean => this.isPathForThisApp(path),
		};
	}

	private createFilterSelectionState(state: IAppState): FilterSelectionState {
		let filterSelectionState = new FilterSelectionState();

		for (const filterKey of Object.keys(state.filterSelection)) {
			filterSelectionState = filterSelectionState.replace(
				filterKey as FilterKey,
				state.filterSelection[filterKey] ?? []
			);
		}

		return filterSelectionState;
	}
}
